import { useState } from "react";
import { Video, PlayCircle, FileText, ListChecks, StickyNote, Link2, Paperclip, CalendarDays, Radio } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { learn, sessionState, isPdf } from "@/lib/learnApi";
import { useOpenMaterial } from "@/components/learn/MaterialViewer";
import { errorText } from "@/lib/api";

const RES = { ppt: [FileText, "Slides"], video: [PlayCircle, "Video"], checklist: [ListChecks, "Checklist"], notes: [StickyNote, "Notes"], link: [Link2, "Link"], file: [Paperclip, "File"] };
const BADGE = { live: "bg-[#0E9384] text-white", upcoming: "bg-sky-50 text-sky-800", completed: "bg-slate-100 text-slate-700", unscheduled: "bg-slate-100 text-slate-600" };
const LABEL = { live: "Live now", upcoming: "Upcoming", completed: "Completed", unscheduled: "Date to be announced" };

export const fmtDate = (iso) => new Date(iso).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export const JoinButton = ({ s, big }) => {
  const st = sessionState(s);
  if (!s.zoom_link || st === "completed") return null;
  return (
    <a data-testid={`join-now-${s.id}`} href={s.zoom_link} target="_blank" rel="noreferrer"
      className={`btn-teal relative inline-flex items-center justify-center gap-2 rounded-xl font-semibold ${big ? "px-8 py-4 text-[17px]" : "px-4 py-2.5 text-[14px]"}`}>
      {st === "live" && <span className="absolute -right-1 -top-1 h-3 w-3 animate-ping rounded-full bg-white" />}
      <Video className="h-4 w-4" /> {st === "live" ? "Join now" : "Join link"}
    </a>
  );
};

export const SessionCard = ({ s, done = [], onProgress }) => {
  const [checked, setChecked] = useState(done);
  const st = sessionState(s);
  const open = useOpenMaterial();
  const toggle = async (i) => {
    const next = checked.includes(i) ? checked.filter((x) => x !== i) : [...checked, i];
    setChecked(next);
    try { await learn.progress(s.id, next); onProgress?.(s.id, next); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <article data-testid={`session-card-${s.n}`} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-4">
          <span className="font-display grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#0B1B33] text-[18px] font-bold text-white">{String(s.n).padStart(2, "0")}</span>
          <div>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${BADGE[st]}`}>{st === "live" && <Radio className="h-3 w-3" />}{LABEL[st]}</span>
            <h3 className="font-display mt-1.5 text-[19px] font-bold leading-tight text-[#0B1B33]">{s.title}</h3>
            {s.starts_at && <p className="mt-1 flex items-center gap-1.5 text-[14px] text-slate-600"><CalendarDays className="h-4 w-4" />{fmtDate(s.starts_at)} · {s.duration_min} min</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <JoinButton s={s} />
          {s.recording_url && <a data-testid={`recording-${s.id}`} href={s.recording_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-[14px] font-semibold text-[#0B1B33] hover:bg-slate-50"><PlayCircle className="h-4 w-4" /> Watch recording</a>}
        </div>
      </div>
      {s.summary && <p className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">{s.summary}</p>}
      {(s.resources || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {s.resources.map((r, i) => {
            const [I, l] = RES[r.type] || RES.link;
            return <button key={i} data-testid={`resource-${s.n}-${i}`} onClick={() => open(r)} className="flex items-center gap-2 rounded-lg bg-teal-50 px-3 py-2 text-[13.5px] font-medium text-[#0B1B33] transition-colors hover:bg-teal-100"><I className="h-4 w-4 text-[#0E9384]" /><span className="text-slate-500">{l}:</span> {r.title}{isPdf(r) && <span className="text-[11px] text-slate-500">(view)</span>}</button>;
          })}
        </div>
      )}
      {(s.checklist || []).length > 0 && (
        <div className="mt-4 rounded-xl bg-slate-50 p-4">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-600">Session checklist · {checked.length}/{s.checklist.length}</p>
          <div className="space-y-2">
            {s.checklist.map((c, i) => (
              <label key={i} className="flex cursor-pointer gap-2.5 text-[14.5px] text-slate-800">
                <Checkbox data-testid={`check-${s.n}-${i}`} checked={checked.includes(i)} onCheckedChange={() => toggle(i)} className="mt-0.5 data-[state=checked]:border-[#0E9384] data-[state=checked]:bg-[#0E9384]" />
                <span className={checked.includes(i) ? "text-slate-500 line-through" : ""}>{c}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};
